<template>
  <div ref="chartDomRef"></div>
</template>

<script setup>
import unitHelper from '@client/helper/unit';
import { usePakHo } from '@client/pages/activity-detail/hooks/usePakHo';
import { xAxisKeyMap } from '@client/store/activityDetailStore';
import { nextTick, ref, watch } from 'vue';
import { useActiveAttributes } from '../useActiveAttributes';

import useDrawChart from '../useDrawChart';
import useFrameSelection from '../useFrameSelection';
import useTimeChart from '../useTimeChart';
import useXAxisConfig from '../useXAxisConfig';
import { TimeChart } from './chart';
import { useListenResize } from '@client/hooks/useBoardResizeEvent';
import { onBeforeUnmount } from 'vue';
import { initStore } from '@client/store/initStore';
import { activityDetailStore } from '@client/store/activityDetailStore';
import * as corosSportConfig from '@coros/sport-config';
const { INDOOR_CLIMB_BOULDERING_CHART_NAME } = corosSportConfig.sport.climb;

const initStoreRef = initStore();
const activityDetailStoreRef = activityDetailStore();
const THEME = initStoreRef.theme;

const chartDataType = 'overview';
const xAxisKey = xAxisKeyMap.time;
const chartRef = ref(null);
const chartDomRef = ref(null);

const { pauseList, tryToDrawChart, chartData } = useDrawChart({ isOverview: true, drawChart });

const { listenFrameSelection, createSelectionZone } = useFrameSelection({ xAxisKey, chartDataType, chartRef });

const { xAxisConfig } = useXAxisConfig({ isPortion: false });
const { chartAttr, activeAttributes } = useActiveAttributes({ isOverview: true });
watch(activeAttributes, () => {
  nextTick(() => {
    tryToDrawChart();
  });
});

const { createMaskLayer, processAttributes } = useTimeChart({
  isOverview: true,
  activeAttributes,
  chartRef
});
const setShowingAttributes = () => {
  let attributes2 = processAttributes(chartAttr.value);
  attributes2 = attributes2.map((item) => {
    const newItem = {
      ...item,
      isOverview: true
    };
    if (item.chartName === INDOOR_CLIMB_BOULDERING_CHART_NAME) {
      newItem.gradeStyles = activityDetailStoreRef.gradeStyles;
    }
    return newItem;
  });
  chartRef.value.setShowingAttributes(attributes2);
};
const { cancelListner } = useListenResize(() => {
  tryToDrawChart();
});
onBeforeUnmount(cancelListner);

// 绘制图表
function drawChart() {
  chartRef.value?.remove();

  chartRef.value = new TimeChart({
    el: chartDomRef.value,
    theme: THEME,
    margin: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    },
    // x轴使用的属性字段名
    xAxisKey,
    // 不显示属性窗口
    showAttrTooltip: false,
    xTickLineSize: 0,
    xTickFontSize: 0,
    // 不绘制x轴刻度线
    showXShortTicks: false,
    // 不绘制x轴刻度文字
    showXTickTexts: false,
    // 不绘制x轴等分线
    showXUniformityLine: false,
    // 不绘制y轴线
    showYLine: false,
    // 不绘制y轴刻度线
    showYTicks: false,
    // 不绘制y轴刻度的文字
    showYTickTexts: false,
    crossHairCallback: crossHairCallback
  });

  const dataList = chartData.value;
  const startX = xAxisConfig.value.startX;
  const endX = xAxisConfig.value.endX;

  const xTicks = xAxisConfig.value.xTicks;

  nextTick(() => {
    listenFrameSelection(dataList);
  });

  // 注入数据
  chartRef.value.setData(dataList);
  // 创建x轴比例尺
  chartRef.value.createXAxis([startX, endX]);
  // 绘制x轴
  chartRef.value.drawXScale(xTicks);
  // 设置显示的属性
  setShowingAttributes();
  // 生成暂停虚线
  chartRef.value.drawPauseLine(pauseList.value);
  // 绘制圈速表选中对应的遮罩
  createMaskLayer();
  // 绘制框选区遮罩
  createSelectionZone();
}

const { crossHairMove } = usePakHo();
function crossHairCallback(e, data) {
  // 暂停区间补充的空数据不传到外部
  if (data.isPauseZoneNullData) {
    return;
  }
  crossHairMove(unitHelper.clone(data));
}
</script>
